// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// bot.ts — the opponent, and a first-class deliverable rather than a placeholder.
//
// THE BOT IS A COMMAND SOURCE, NOT A SECOND GAME. It emits exactly the same four-integer commands a
// thumb does, into exactly the same log, and the simulation cannot tell the difference. That is what
// makes solo, multiplayer and the headless balance sim ONE code path — and it is what lets
// tests/balance.test.ts referee the bot that actually ships instead of a toy written to be measured.
//
// This is real time, so there is no minimax. Three layers, all integer, all deterministic:
//
//   0. A threat map, stamped from a baked kernel by addition. No exponentials, no distances.
//   1. A posture, chosen once a second by integer utility with hysteresis so it cannot oscillate.
//   2. Per-unit goals, staggered by entity id so twelve units never all re-path on one tick.
//
// The tiers differ in COMPETENCE, not information: there is no fog of war in Salient, so the bot
// sees exactly what the player sees because the player sees everything. The one exception is
// declared in the table below and it is a penalty, not a cheat.

import {
  Cmd,
  Stance,
  aliveCount,
  canBuild,
  canSee,
  opacity,
  tileOf,
  seatKey,
  tonnage,
  type State,
} from './sim';
import { COLS, ROWS, TILES, Terrain, chebyshev, colOf, idx, rowOf } from './map';
import { UNITS, UnitType } from './units';
import type { Command } from './commands';

export interface Tier {
  id: string;
  name: string;
  /** Ticks between posture re-evaluations. */
  posture: number;
  /** Ticks between a unit reconsidering where it is going. */
  cadence: number;
  /** Percent income the bot is allowed. The only handicap in the game, and only on the easy tier. */
  income: number;
  /** Does it put Arbalests on HOLD where they can see, instead of walking them into a brawl? */
  discipline: boolean;
  /** Does it route around threat instead of taking the shortest path? */
  wary: boolean;
  /** Tonnage deficit at which it stops accepting a fight and falls back. */
  accept: number;
  blurb: string;
}

export const TIERS: readonly Tier[] = [
  {
    id: 'raw',
    name: 'Raw',
    posture: 60,
    cadence: 30,
    income: 80,
    discipline: false,
    wary: false,
    accept: -400,
    blurb: 'Builds a real army and throws it into one bad fight. Poorer than you, and says so.',
  },
  {
    id: 'steady',
    name: 'Steady',
    posture: 20,
    cadence: 8,
    income: 100,
    discipline: true,
    wary: false,
    accept: -120,
    blurb: 'Holds its artillery where it can see, contests the middle, and takes even trades.',
  },
  {
    id: 'hard',
    name: 'Hard',
    posture: 10,
    cadence: 4,
    income: 100,
    discipline: true,
    wary: true,
    accept: 0,
    blurb: 'Plays the salvage exchange — it will decline a fight it wins on kills and loses on wrecks.',
  },
];

export const TIER_IDS: readonly string[] = TIERS.map((t) => t.id);
export const DEFAULT_TIER = 'steady';

export function tierOf(id: string | null | undefined): Tier {
  const i = TIER_IDS.indexOf(typeof id === 'string' ? id : '');
  return TIERS[i >= 0 ? i : 1];
}

const enum Posture {
  Expand = 0,
  Push = 1,
  Raid = 2,
  Salvage = 3,
  Turtle = 4,
}

/**
 * A baked 7x7 falloff, applied by addition. This is what an exponential would have been, computed
 * once at authoring time and pasted in — because `Math.exp` in a loop the bot runs two hundred times
 * a match is the easiest way to make two devices disagree about where it is safe to stand.
 */
const KERNEL: readonly number[] = [
  0, 1, 2, 3, 2, 1, 0,
  1, 3, 6, 8, 6, 3, 1,
  2, 6, 12, 16, 12, 6, 2,
  3, 8, 16, 20, 16, 8, 3,
  2, 6, 12, 16, 12, 6, 2,
  1, 3, 6, 8, 6, 3, 1,
  0, 1, 2, 3, 2, 1, 0,
];

export interface BotMind {
  seat: number;
  tier: Tier;
  posture: Posture;
  threat: Int16Array;
  threatAt: number;
  /**
   * A seeded personality, and the reason the balance sim can measure anything at all.
   *
   * The simulation is now exactly symmetric — tests/mirror.test.ts proves two identical bots stay
   * pixel-perfect 180° images of each other for a whole match — which is the fairness property you
   * want and a useless measurement: every game is a draw. So the BOT (never the sim) carries a
   * little seeded noise, in the phase of its thinking and in its posture scores. It is deterministic,
   * it is reproducible from the match seed, and it is not an advantage to either seat, because the
   * sweep runs every seed with the personalities swapped between the seats.
   */
  rnd: number;
  phase: number;
}

/** xorshift32. Deterministic, integer, and confined to the bot — the simulation draws no randomness. */
function nextRnd(m: BotMind): number {
  let x = m.rnd | 0;
  x ^= x << 13;
  x ^= x >>> 17;
  x ^= x << 5;
  m.rnd = x | 0;
  return (x >>> 0) % 1000;
}

/**
 * The personality is derived from `seed` ALONE, never from `seat`.
 *
 * Folding the seat into it looks like a harmless way to make the two bots differ, and it makes the
 * simulation asymmetric on tick one: the two sides think on different phases for the whole match,
 * which is exactly the bias tests/mirror.test.ts exists to forbid. Callers that WANT two different
 * personalities pass two different seeds; callers that want a strict mirror pass the same one.
 */
export const createMind = (seat: number, tier: Tier, seed = 0): BotMind => {
  const r = (Math.imul(seed | 0, 0x85ebca6b) ^ 0x1234567) | 0;
  return {
    seat,
    tier,
    posture: Posture.Expand,
    threat: new Int16Array(TILES),
    threatAt: -1000,
    rnd: r === 0 ? 1 : r,
    phase: (r >>> 8) % 7,
  };
};

function stampThreat(s: State, m: BotMind): void {
  m.threat.fill(0);
  for (let e = 0; e < s.nextId; e++) {
    if (!s.alive[e] || s.seat[e] === m.seat) continue;
    const spec = UNITS[s.type[e]];
    const dps = ((spec.damage * 20) / spec.interval) | 0;
    const t = tileOf(s, e);
    const c0 = colOf(t);
    const r0 = rowOf(t);
    for (let k = 0; k < 49; k++) {
      const w = KERNEL[k];
      if (w === 0) continue;
      const c = c0 + (k % 7) - 3;
      const r = r0 + ((k / 7) | 0) - 3;
      if (c < 0 || c >= COLS || r < 0 || r >= ROWS) continue;
      m.threat[idx(c, r)] += (dps * w) / 4;
    }
  }
}

const forgeAnchor = (seat: number): number => (seat === 0 ? idx(5, 14) : idx(6, 1));

/**
 * Every tile this side would like to be standing on to take ground.
 *
 * The Foundry is ONE seam with an eight-tile footprint, and it is expanded here rather than
 * represented by its anchor. Sending both seats at the anchor tile is a seat bias: the anchor is not
 * the centre of the board, so one side has a shorter walk to it, and tests/mirror.test.ts caught
 * exactly that as a nine-sub-unit divergence forty-six ticks into every theline match.
 */
function seamGoals(s: State, seat: number): number[] {
  const out: number[] = [];
  for (const seam of s.seams) {
    if (seam.owner === seat || seam.ore <= 0) continue;
    if (seam.foundry) out.push(...s.map.foundryTiles);
    else out.push(seam.tile);
  }
  return out;
}

function pickPosture(s: State, m: BotMind): Posture {
  const me = tonnage(s, m.seat);
  const them = tonnage(s, 1 - m.seat);
  const lead = me - them;

  let wreckOre = 0;
  for (const w of s.wrecks) wreckOre += w.ore;
  let openSeam = 0;
  for (const seam of s.seams) if (seam.owner !== m.seat && seam.ore > 0) openSeam += seam.ore;
  let enemySeam = 0;
  for (const seam of s.seams) if (seam.owner === 1 - m.seat && seam.ore > 0) enemySeam += seam.ore;

  const scores = [
    (openSeam / 4) | 0,
    lead * 2 + (aliveCount(s, m.seat) - aliveCount(s, 1 - m.seat)) * 40,
    (enemySeam / 6) | 0,
    wreckOre * 3,
    -lead * 2 + (s.mode.forgeKill ? ((1000 - s.sides[m.seat].forgeHp) / 4) | 0 : 0),
  ];
  // Hysteresis: a posture that is already running gets a quarter of its own score for free, so the
  // bot does not spend a match oscillating between raiding and turtling on a two-point difference.
  scores[m.posture] += Math.abs(scores[m.posture] / 4) | 0;
  // A few points of seeded personality, so two bots of the same tier do not play the identical game.
  for (let i = 0; i < scores.length; i++) scores[i] += nextRnd(m) % 24;

  let best = 0;
  for (let i = 1; i < scores.length; i++) if (scores[i] > scores[best]) best = i;
  return best as Posture;
}

/**
 * Where one unit should be standing, given the posture. Not a path — just a destination; the
 * simulation's own A* does the walking, using the same code the player's orders use.
 */
function goalFor(s: State, m: BotMind, e: number, op: Uint8Array): number {
  const seat = m.seat;
  const here = tileOf(s, e);
  const type = s.type[e];

  // Artillery wants to see, not to arrive. Score every reachable-ish tile by how many enemies it
  // could shoot from there, less how dangerous it is; then stand still.
  if (type === UnitType.Arbalest && m.tier.discipline) {
    let best = here;
    let bestScore = -(1 << 30);
    for (let t = 0; t < TILES; t++) {
      if (s.map.terrain[t] === Terrain.Ridge || s.map.terrain[t] === Terrain.Forge) continue;
      if (s.map.terrain[t] === Terrain.Scrub) continue;
      if (chebyshev(t, here) > 6) continue;
      let seen = 0;
      for (let f = 0; f < s.nextId; f++) {
        if (!s.alive[f] || s.seat[f] === seat) continue;
        const ft = tileOf(s, f);
        if (chebyshev(t, ft) > 4) continue;
        if (canSee(op, t, ft)) seen++;
      }
      const sc = seen * 200 - m.threat[t] - chebyshev(t, here) * 6;
      if (sc > bestScore || (sc === bestScore && seatKey(seat, t) < seatKey(seat, best))) {
        bestScore = sc;
        best = t;
      }
    }
    if (bestScore > 0) return best;
  }

  // An Anvil is a wall, so its job is to be BETWEEN the enemy and its own artillery.
  if (type === UnitType.Anvil) {
    let arb = -1;
    let arbD = 1 << 30;
    for (let f = 0; f < s.nextId; f++) {
      if (!s.alive[f] || s.seat[f] !== seat || s.type[f] !== UnitType.Arbalest) continue;
      const d = chebyshev(here, tileOf(s, f));
      if (d < arbD || (d === arbD && f < arb)) {
        arbD = d;
        arb = f;
      }
    }
    if (arb >= 0) {
      const at = tileOf(s, arb);
      let best = at;
      let bestScore = -(1 << 30);
      for (let dc = -1; dc <= 1; dc++) {
        for (let dr = -1; dr <= 1; dr++) {
          if (dc === 0 && dr === 0) continue;
          const c = colOf(at) + dc;
          const r = rowOf(at) + dr;
          if (c < 0 || c >= COLS || r < 0 || r >= ROWS) continue;
          const t = idx(c, r);
          if (s.map.terrain[t] === Terrain.Ridge || s.map.terrain[t] === Terrain.Forge) continue;
          const sc = m.threat[t];
          if (sc > bestScore || (sc === bestScore && seatKey(seat, t) < seatKey(seat, best))) {
            bestScore = sc;
            best = t;
          }
        }
      }
      if (bestScore > 0) return best;
    }
  }

  const goals = seamGoals(s, seat);
  const nearest = (list: readonly number[]): number => {
    let best = -1;
    let bestD = 1 << 30;
    for (const t of list) {
      const d = chebyshev(here, t) * 10 + (m.tier.wary ? (m.threat[t] / 8) | 0 : 0);
      if (d < bestD || (d === bestD && seatKey(seat, t) < seatKey(seat, best < 0 ? t : best))) {
        bestD = d;
        best = t;
      }
    }
    return best;
  };

  switch (m.posture) {
    case Posture.Salvage: {
      const t = nearest(s.wrecks.map((w) => w.tile));
      if (t >= 0) return t;
      break;
    }
    case Posture.Raid: {
      const raid: number[] = [];
      for (const x of s.seams) {
        if (x.owner !== 1 - seat || x.ore <= 0) continue;
        if (x.foundry) raid.push(...s.map.foundryTiles);
        else raid.push(x.tile);
      }
      const t = nearest(raid);
      if (t >= 0) return t;
      break;
    }
    case Posture.Turtle:
      return s.sides[seat].rally;
    case Posture.Push: {
      let best = -1;
      let bestD = 1 << 30;
      for (let f = 0; f < s.nextId; f++) {
        if (!s.alive[f] || s.seat[f] === seat) continue;
        const t = tileOf(s, f);
        const d = chebyshev(here, t);
        if (d < bestD || (d === bestD && seatKey(seat, t) < seatKey(seat, best < 0 ? t : best))) {
          bestD = d;
          best = t;
        }
      }
      if (best >= 0) return best;
      if (s.mode.forgeKill) return forgeAnchor(1 - seat);
      break;
    }
    default:
      break;
  }
  const t = nearest(goals);
  return t >= 0 ? t : s.sides[seat].rally;
}

/** Composition response. No lookahead: build whatever you are furthest short of and can afford. */
function wantedType(s: State, seat: number): number {
  const mine = [0, 0, 0];
  const theirs = [0, 0, 0];
  for (let e = 0; e < s.nextId; e++) {
    if (!s.alive[e]) continue;
    (s.seat[e] === seat ? mine : theirs)[s.type[e]]++;
  }
  for (const q of s.sides[seat].queue) mine[q]++;
  const want = [
    2 + ((theirs[UnitType.Arbalest] * 3) / 2) | 0,
    1 + theirs[UnitType.Anvil],
    2 + ((theirs[UnitType.Harrier] / 2) | 0),
  ];
  let best = -1;
  let bestGap = -(1 << 30);
  for (let t = 0; t < 3; t++) {
    if (!canBuild(s, seat, t)) continue;
    const gap = want[t] - mine[t];
    if (gap > bestGap || (gap === bestGap && t < best)) {
      bestGap = gap;
      best = t;
    }
  }
  return bestGap > 0 ? best : -1;
}

/**
 * In a mode with a LIFETIME PRESS BUDGET, spend it like one.
 *
 * lastbatch gives six presses for the whole match and makes ore abundant, so the naive "build
 * whatever you are short of, whenever you can afford it" policy burns all six inside three seconds
 * — and then twelve bodies meet at once and the match is over in FIFTY-SIX SECONDS, which the
 * round-length gate caught. Rationing the presses against the clock is what turns the mode into the
 * thing it is supposed to be: a four-minute match where each of six decisions is a bet on what the
 * board will look like when it lands.
 */
function holdingPresses(s: State, seat: number): boolean {
  const budget = s.mode.buildBudget;
  if (budget <= 0) return false;
  const side = s.sides[seat];
  const spentShare = side.presses / budget;
  const timeShare = s.tick / s.mode.hardEnd;
  // Always press when the army is genuinely thin, or when the budget would otherwise expire unused.
  if (aliveCount(s, seat) + side.queue.length <= 3) return false;
  if (timeShare > 0.75) return false;
  return spentShare > timeShare + 0.34;
}

/**
 * One bot decision pass. Appends commands for the CURRENT tick — the bot has no input delay, which
 * is not an advantage: it is what "the bot is a local command source" means, and the balance sim
 * runs both seats through this same function so any edge it conferred would show up as a seat bias.
 */
export function botStep(s: State, m: BotMind, out: Command[]): void {
  if (s.over) return;
  const seat = m.seat;
  const tier = m.tier;

  if (s.tick - m.threatAt >= tier.posture) {
    m.threatAt = s.tick;
    stampThreat(s, m);
    m.posture = pickPosture(s, m);
  }

  if (s.tick % 10 === 0 && !holdingPresses(s, seat)) {
    const type = wantedType(s, seat);
    if (type >= 0) out.push({ tick: s.tick, seat, kind: Cmd.Build, unit: -1, arg: type });
  }

  const op = opacity(s);
  const behind = tonnage(s, seat) - tonnage(s, 1 - seat) < tier.accept;

  for (let e = 0; e < s.nextId; e++) {
    if (!s.alive[e] || s.seat[e] !== seat) continue;
    // Staggered by id so the whole army never re-plans on one tick — which is both a frame-time
    // saving and the reason the bot's army does not move like a single organism.
    if ((s.tick + s.ord[e] * 3 + m.phase) % tier.cadence !== 0) continue;

    // HOLD ONLY WHILE THE TARGET IS STILL AT ARM'S LENGTH.
    //
    // The first version held an Arbalest still whenever it had a target at all, which measured as a
    // 360-ore ADVANTAGE to the ablated control — the discipline was actively making the bot worse,
    // because a seventy-hit-point artillery piece standing perfectly still while a Harrier closes to
    // one tile is a free kill. Holding is only correct at range; inside it, the right answer is to
    // stop being there.
    let closest = 1 << 30;
    for (let f = 0; f < s.nextId; f++) {
      if (!s.alive[f] || s.seat[f] === seat) continue;
      const d = chebyshev(tileOf(s, e), tileOf(s, f));
      if (d < closest) closest = d;
    }
    const wantHold =
      tier.discipline && s.type[e] === UnitType.Arbalest && s.foe[e] >= 0 && s.alive[s.foe[e]] && closest >= 3;
    const stance = wantHold ? Stance.Hold : Stance.Push;
    if (s.stance[e] !== stance) out.push({ tick: s.tick, seat, kind: Cmd.Stance, unit: e, arg: stance });
    if (wantHold) continue;

    // A hurt body on the hard tier walks home rather than handing over a wreck. On the raw tier it
    // fights to the death, which is a real mistake and is meant to be one.
    const hurt = s.hp[e] * 4 < UNITS[s.type[e]].hp;
    // An Arbalest with something in its face walks home rather than trading at knife range, whatever
    // the posture says. This is the other half of the discipline fix.
    const crowded = tier.discipline && s.type[e] === UnitType.Arbalest && closest <= 2;
    const goal = behind || crowded || (hurt && tier.wary) ? s.sides[seat].rally : goalFor(s, m, e, op);
    // Only speak when the order CHANGES. A bot that re-issued the same move every four ticks would
    // put forty thousand integers in a log that gets re-canonicalised on every network message.
    if (goal >= 0 && goal !== s.dest[e]) out.push({ tick: s.tick, seat, kind: Cmd.Move, unit: e, arg: goal });
  }
}
